import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { ExportFormatButtons } from './buttons/ExportFormatButton';
import { ArtifactPageTabs } from './ArtifactPageTabs';
import { ArtifactContent } from './ArtifactContent';

import { cn } from '@/lib/utils';
import { applyTheme } from '@/artifact/theme';
import { downloadExportPayload, exportSurfaceSnapshot, type ArtifactExportFormat } from '@/artifact/export';
import { trackArtifactExported } from '@/analytics';
import type { ArtifactEngine } from '@/hooks/useArtifactEngine';

import { ConnectionStatus } from './labels/ConnectionStatus';
import { ArtifactEmptyState } from './labels/ArtifactEmptyState';

import '@/artifact/artifact.css';

interface LandscapeArtifactViewProps {
  engine: ArtifactEngine;
  onClose: () => void;
}

export function LandscapeArtifactView({ engine, onClose }: LandscapeArtifactViewProps) {
  const {
    store,
    connected,
    pages,
    viewingPageIndex,
    liveComponentsSize,
    rootCuids,
    focusTarget,
    hasComponents,
    hasPages,
    callbacks,
    theme,
    setSurfaceGetter,
    deletePage,
  } = engine;

  const containerRef = useRef<HTMLDivElement>(null);
  const [exportingFormat, setExportingFormat] = useState<ArtifactExportFormat | null>(null);
  const [chromeVisible, setChromeVisible] = useState(true);
  const chromeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const getSurfaceElement = useCallback((): HTMLElement | null => {
    if (!containerRef.current) return null;
    const el = containerRef.current.querySelector('.a-surface');
    return el instanceof HTMLElement ? el : null;
  }, []);

  useEffect(() => {
    setSurfaceGetter(getSurfaceElement);
    return () => setSurfaceGetter(null);
  }, [getSurfaceElement, setSurfaceGetter]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      applyTheme(theme, containerRef.current, true);
    }
  }, [theme]);

  useEffect(() => {
    chromeTimerRef.current = setTimeout(() => setChromeVisible(false), 3000);
    return () => clearTimeout(chromeTimerRef.current);
  }, []);

  const handleTap = useCallback(() => {
    setChromeVisible((v) => {
      if (!v) {
        clearTimeout(chromeTimerRef.current);
        chromeTimerRef.current = setTimeout(() => setChromeVisible(false), 3000);
      }
      return !v;
    });
  }, []);

  const handleDownload = useCallback(async (format: ArtifactExportFormat) => {
    const surface = getSurfaceElement();
    if (!surface || exportingFormat !== null) return;
    trackArtifactExported(format);
    setExportingFormat(format);
    try {
      const payload = await exportSurfaceSnapshot(surface, format);
      const stamp = new Date().toISOString().replaceAll(':', '-');
      downloadExportPayload(payload, `panel-${stamp}.${format}`);
    } finally {
      setExportingFormat(null);
    }
  }, [exportingFormat, getSurfaceElement]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-zinc-950 text-zinc-100">
      {/* Page tabs — always visible at top */}
      {hasPages && (
        <ArtifactPageTabs
          pages={pages}
          viewingPageIndex={viewingPageIndex}
          liveComponentsSize={liveComponentsSize}
          onSelectPage={(index) => store.dispatch({ type: 'SET_VIEWING_PAGE', index })}
          onDeletePage={(idx) => void deletePage(idx)}
          classNames={{
            root: "relative z-20 shrink-0 items-center bg-zinc-950 px-3 py-1.5",
            tab: "rounded-lg px-2.5 py-1 pr-6 text-[11px] font-medium",
            tabActive: "bg-white/[0.1] text-zinc-200",
            tabInactive: "active:bg-white/[0.06]",
            delete: "h-5 w-5 active:text-zinc-300 active:bg-white/[0.08] opacity-0 group-hover/tab:opacity-100",
            liveTab: "rounded-lg px-2.5 py-1 text-[11px] font-medium",
          }}
        />
      )}

      {/* Remaining area: chrome overlay + content */}
      <div className="relative flex-1 overflow-hidden">
        {/* Auto-hiding chrome overlay */}
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-between px-3 py-2 transition-opacity duration-300',
            chromeVisible ? 'opacity-100' : 'opacity-0',
          )}
        >
          <button
            type="button"
            onClick={onClose}
            className="pointer-events-auto flex h-8 items-center gap-1.5 rounded-full bg-black/60 px-3 text-[13px] text-zinc-300 backdrop-blur-sm transition-colors active:bg-black/80"
          >
            <ArrowLeft className="h-4 w-4" />
            Chat
          </button>

          <div className="pointer-events-auto flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 backdrop-blur-sm">
              <ConnectionStatus connected={connected} labelClassName="text-zinc-400" />
            </div>

            {hasComponents && (
              <ExportFormatButtons
                formats={['png', 'pdf']}
                disabled={exportingFormat !== null}
                onDownload={handleDownload}
                buttonClassName="h-7 rounded-full bg-black/60 px-2.5 text-zinc-400 backdrop-blur-sm active:bg-black/80"
              />
            )}
          </div>
        </div>

        {/* Tap target to toggle chrome */}
        <div className="absolute inset-0 z-[5]" onClick={handleTap} />

        {/* Artifact content */}
        <ArtifactContent
          store={store}
          callbacks={callbacks}
          rootCuids={rootCuids}
          focusTarget={focusTarget}
          containerRef={containerRef}
          className="relative z-[6] h-full"
          surfaceClassName="h-full"
          emptyState={<ArtifactEmptyState title="Waiting for the agent to render components..." />}
        />
      </div>
    </div>
  );
}
